import React, { useEffect, useRef, useState } from 'react';
import { apiFetch } from '@/lib/supabaseClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Mail, ShieldCheck, Send, PauseCircle, PlayCircle, Archive, RotateCcw, LogOut, X, Plus, Sliders, ChevronDown, Save } from 'lucide-react';
import { toast } from 'sonner';
import SecurityEditorModal from './SecurityEditorModal';
import { hasCap } from '@/lib/capabilities';
import { useEffectiveMe } from '@/lib/effectiveMe';

const ACCESS_MODULE_LABELS = {
  attendance: 'Presenze',
  company: 'Azienda',
  department: 'Reparti',
  ferie: 'Ferie',
  location: 'Locali',
  orders: 'Ordini',
  personale: 'Personale',
  security: 'Utenti e Accessi',
  turni: 'Turni',
  warehouse: 'Magazzino',
};

const accessModuleLabel = (module) => ACCESS_MODULE_LABELS[module] || module;

const ACCESS_MODULE_ORDER = [
  'ferie',
  'warehouse',
  'orders',
  'personale',
  'attendance',
  'turni',
  'security',
];

const ROLE_DESCRIPTIONS = {
  DIPENDENTE: 'Accesso operativo limitato alla propria Persona e alle funzioni assegnate dal ruolo.',
  RESP_LOCALE: 'Gestisce le attività autorizzate nel perimetro di locale e reparto selezionato.',
  RESP_GEN: 'Gestisce le attività autorizzate nel perimetro aziendale selezionato.',
  SEGRETERIA: 'Accesso amministrativo alle funzioni consentite dal ruolo e dal perimetro assegnato.',
};

function ChangeEmailModal({ legacyId, currentEmail, onClose, onSaved }) {
  const [newEmail, setNewEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    if (e) e.preventDefault();
    setErr('');
    const n = newEmail.trim().toLowerCase();
    const c = confirmEmail.trim().toLowerCase();
    if (!n) { setErr('Inserisci la nuova email'); return; }
    if (n !== c) { setErr('Le due email non coincidono'); return; }
    if (n === (currentEmail || '').toLowerCase()) { setErr("La nuova email coincide con quella attuale"); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(n)) { setErr('Email non valida'); return; }
    setBusy(true);
    try {
      await apiFetch(`/api/personale/${legacyId}/access/change-email`, {
        method: 'POST',
        body: JSON.stringify({ new_email: n }),
      });
      toast.success('Email accesso aggiornata');
      onSaved?.();
    } catch (e2) {
      const msg = e2.message || 'Impossibile aggiornare l\'email';
      setErr(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="modal-change-email" role="dialog" aria-modal="true" aria-labelledby="change-email-title" onClick={onClose} onKeyDown={(e) => e.key === 'Escape' && onClose()}>
      <div className="w-full max-w-md rounded-2xl border border-app-border bg-app-surface p-5 sm:p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div id="change-email-title" className="text-xs uppercase tracking-[0.2em] font-bold text-brand-green">Cambia email accesso</div>
          <button onClick={onClose} data-testid="btn-close-change-email" aria-label="Chiudi" className="inline-flex h-10 w-10 items-center justify-center rounded-full text-stone-400 hover:bg-stone-50 hover:text-brand-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200">
            <X className="w-5 h-5 text-stone-400 hover:text-brand-navy" />
          </button>
        </div>
        <div className="rounded-xl bg-stone-50 border border-stone-200 p-3 text-xs text-stone-600 mb-3">
          <div className="font-semibold text-brand-navy">Email attuale</div>
          <div data-testid="change-email-current" className="mt-0.5 break-all">{currentEmail || '—'}</div>
        </div>
        <form onSubmit={submit} className="space-y-3" data-testid="change-email-form">
          <div>
            <Label htmlFor="ce-new">Nuova email *</Label>
            <Input id="ce-new" type="email" required autoComplete="off"
                   value={newEmail} onChange={(e) => { setNewEmail(e.target.value); setErr(''); }}
                   data-testid="change-email-new" placeholder="nome@azienda.it" className="h-11" />
          </div>
          <div>
            <Label htmlFor="ce-conf">Conferma nuova email *</Label>
            <Input id="ce-conf" type="email" required autoComplete="off"
                   value={confirmEmail} onChange={(e) => { setConfirmEmail(e.target.value); setErr(''); }}
                   data-testid="change-email-confirm" placeholder="Ripeti la nuova email" className="h-11" />
          </div>
          {err && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="change-email-error">{err}</div>
          )}
          <Button type="submit" disabled={busy || !newEmail || !confirmEmail}
                  className="w-full h-11 bg-brand-green hover:bg-brand-green-hover text-white gap-2"
                  data-testid="btn-save-email">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            {busy ? 'Salvataggio…' : 'Salva email'}
          </Button>
          <p className="text-[11px] text-stone-500 text-center">L'auth_user_id resta invariato. Ruolo, scope, permessi e storico sono preservati.</p>
        </form>
      </div>
    </div>
  );
}

const STATUS_MAP = {
  INVITATO:   { label: 'Invitato',    tone: 'bg-amber-100 text-amber-800 border-amber-200' },
  ATTIVO:     { label: 'Attivo',      tone: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  SOSPESO:    { label: 'Sospeso',     tone: 'bg-red-100 text-red-800 border-red-200' },
  ARCHIVIATO: { label: 'Archiviato',  tone: 'bg-stone-100 text-stone-600 border-stone-200' },
  SCONOSCIUTO:{ label: 'Sconosciuto', tone: 'bg-stone-100 text-stone-600 border-stone-200' },
};

function fmtDate(v) { return v ? new Date(v).toLocaleString('it-IT') : '—'; }

export default function AccessoTab({ p, onChanged }) {
  const me = useEffectiveMe();
  const canCreate = hasCap(me, 'security.users.create');
  const canManage = hasCap(me, 'security.users.update');

  const [access, setAccess] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [showChangeEmail, setShowChangeEmail] = useState(false);
  const [busy, setBusy] = useState(null);
  const userId = p.account?.id || null;
  const mountedRef = useRef(false);
  const loadVersionRef = useRef(0);

  const load = async () => {
    const loadVersion = ++loadVersionRef.current;
    try {
      const data = await apiFetch(`/api/personale/${p.legacy_id}/access`);
      if (mountedRef.current && loadVersion === loadVersionRef.current) {
        setAccess(data);
      }
    } catch (e) {
      if (mountedRef.current && loadVersion === loadVersionRef.current) {
        toast.error(e.message || 'Errore caricamento accesso');
        setAccess({ has_account: false });
      }
    }
  };
  useEffect(() => {
    mountedRef.current = true;
    setAccess(null);
    load();
    return () => {
      mountedRef.current = false;
      loadVersionRef.current += 1;
    };
    // The access view must discard the previous person's late response.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.legacy_id]);

  const action = async (key, path, body) => {
    if (!window.confirm(confirmText(key))) return;
    setBusy(key);
    try {
      // Success is granted ONLY when the backend actually returns 2xx. apiFetch
      // throws on !res.ok, so no fake success can slip through here.
      const resp = await apiFetch(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined });
      // Timestamp is refreshed via load() → server returns last_invite_sent_at
      // computed from audit_log, which is only inserted after a real 200 from Supabase.
      toast.success(successText(key), { description: resp?.sent_at ? `Inviato: ${new Date(resp.sent_at).toLocaleString('it-IT')}` : undefined });
      await load(); onChanged?.();
    } catch (e) {
      // No optimistic UI update: state is not mutated, timestamps stay pinned to
      // the last real successful send. Surface the real backend error verbatim.
      const msg = e?.status === 429
        ? (e.message || 'Limite email raggiunto — riprova più tardi o configura SMTP custom.')
        : e?.status === 409
        ? (e.message || 'Operazione non consentita.')
        : (e?.message || 'Operazione fallita');
      toast.error(msg, { duration: 8000, description: e?.status ? `HTTP ${e.status}` : undefined });
    } finally { setBusy(null); }
  };

  if (access === null) {
    return <div id="panel-accesso" role="tabpanel" tabIndex="0" className="flex items-center gap-2 rounded-2xl border border-app-border bg-app-surface p-4 text-stone-500 sm:p-6" data-testid="panel-accesso"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</div>;
  }

  if (!access.has_account) {
    return (
      <div id="panel-accesso" role="tabpanel" tabIndex="0" className="space-y-3 rounded-2xl border border-app-border bg-app-surface p-4 sm:p-6" data-testid="panel-accesso">
        <div className="text-xs uppercase tracking-wider text-stone-400 font-semibold">Accesso RestaurantOS</div>
        <p className="text-sm text-stone-600">Questa persona non ha un account RestaurantOS.</p>
        {canCreate ? (
          <Button onClick={() => setShowCreate(true)} className="bg-brand-green hover:bg-brand-green-hover text-white gap-1" data-testid="btn-create-access">
            <Plus className="w-4 h-4" /> Dai accesso
          </Button>
        ) : (
          <p className="text-xs text-stone-500">Solo un utente autorizzato può creare l'accesso.</p>
        )}
        {showCreate && (
          <CreateAccessModal
            person={p}
            onClose={() => setShowCreate(false)}
            onDone={() => { setShowCreate(false); load(); onChanged?.(); }}
          />
        )}
      </div>
    );
  }

  const s = STATUS_MAP[access.status] || STATUS_MAP.SCONOSCIUTO;
  const isSuspended = access.status === 'SOSPESO';
  const isArchived = access.status === 'ARCHIVIATO';

  return (
    <div id="panel-accesso" role="tabpanel" tabIndex="0" className="space-y-4 rounded-2xl border border-app-border bg-app-surface p-4 sm:p-6" data-testid="panel-accesso">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-wider text-stone-400 font-semibold mb-2">Accesso RestaurantOS</div>
          <div className="flex items-center gap-2 text-base text-brand-navy font-medium">
            <Mail className="w-4 h-4 text-brand-blue" /> <span data-testid="access-email" className="break-all">{access.email}</span>
          </div>
          {access.full_name && <div className="text-xs text-stone-500 mt-0.5">Nome account: {access.full_name}</div>}
        </div>
        <span className={`text-xs px-2 py-1 rounded-full border font-semibold ${s.tone}`} data-testid="access-status">{s.label}</span>
      </div>

      {access.roles.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wider text-stone-400 font-semibold mb-1">Ruolo / Scope</div>
          <div className="flex flex-wrap gap-1">
            {access.roles.map((r, i) => (
              <span key={i} data-testid={`access-role-${i}`} className="text-xs px-2 py-0.5 rounded-full bg-brand-green/10 text-brand-green font-semibold">
                {r.role_code}
                {r.company_name && ` · ${r.company_name}`}
                {r.location_name && ` · ${r.location_name}`}
                {r.department_name && ` · ${r.department_name}`}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 text-xs">
        <div><span className="text-stone-400">Ultimo accesso:</span> <span className="text-brand-navy font-medium" data-testid="access-last-signin">{fmtDate(access.last_sign_in_at)}</span></div>
        <div>
          <span className="text-stone-400">Ultimo invio invito:</span>{' '}
          <span className="text-brand-navy font-medium" data-testid="access-last-invite">
            {fmtDate(access.last_invite_sent_at || access.invited_at)}
          </span>
        </div>
      </div>

      {canManage && (
        <div className="flex flex-wrap gap-2 pt-2 border-t border-stone-100">
          {access.status === 'INVITATO' && (
            <Button size="sm" variant="outline" disabled={busy === 'resend'} onClick={() => action('resend', `/api/personale/${p.legacy_id}/access/resend-invite`)}
              className="text-brand-blue border-brand-blue/30 hover:bg-brand-blue/5 gap-1" data-testid="btn-resend-invite">
              {busy === 'resend' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Reinvia invito
            </Button>
          )}
          {access.status === 'ATTIVO' && (
            <Button size="sm" variant="outline" disabled={busy === 'recover'} onClick={() => action('recover', `/api/personale/${p.legacy_id}/access/recover-access`)}
              className="text-brand-blue border-brand-blue/30 hover:bg-brand-blue/5 gap-1" data-testid="btn-recover-access">
              {busy === 'recover' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Recupera accesso
            </Button>
          )}
          {!isSuspended && !isArchived && (
            <Button size="sm" variant="outline" disabled={busy === 'suspend'} onClick={() => action('suspend', `/api/personale/${p.legacy_id}/access/suspend`, { reason: null })}
              className="text-red-700 border-red-200 hover:bg-red-50 gap-1" data-testid="btn-account-suspend">
              <PauseCircle className="w-3.5 h-3.5" /> Sospendi
            </Button>
          )}
          {isSuspended && (
            <Button size="sm" variant="outline" disabled={busy === 'reactivate'} onClick={() => action('reactivate', `/api/personale/${p.legacy_id}/access/reactivate`)}
              className="text-emerald-700 border-emerald-200 hover:bg-emerald-50 gap-1" data-testid="btn-account-reactivate">
              <PlayCircle className="w-3.5 h-3.5" /> Riattiva
            </Button>
          )}
          {!isArchived && (
            <Button size="sm" variant="outline" disabled={busy === 'archive'} onClick={() => action('archive', `/api/personale/${p.legacy_id}/access/archive`)}
              className="text-stone-700 border-stone-300 hover:bg-stone-50 gap-1" data-testid="btn-archive-access">
              <Archive className="w-3.5 h-3.5" /> Archivia
            </Button>
          )}
          {isArchived && (
            <Button size="sm" variant="outline" disabled={busy === 'restore'} onClick={() => action('restore', `/api/personale/${p.legacy_id}/access/restore`)}
              className="text-brand-green border-brand-green/30 hover:bg-brand-green/5 gap-1" data-testid="btn-restore-access">
              <RotateCcw className="w-3.5 h-3.5" /> Ripristina
            </Button>
          )}
          <Button size="sm" variant="outline" disabled={busy === 'revoke'} onClick={() => action('revoke', `/api/personale/${p.legacy_id}/access/revoke-sessions`)}
            className="text-stone-600 border-stone-300 hover:bg-stone-50 gap-1" data-testid="btn-revoke-sessions">
            <LogOut className="w-3.5 h-3.5" /> Revoca sessioni
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowChangeEmail(true)}
            className="text-brand-navy border-stone-300 hover:bg-stone-50 gap-1" data-testid="btn-change-email">
            <Mail className="w-3.5 h-3.5" /> Cambia email
          </Button>
           {userId && (
             <Button size="sm" onClick={() => setShowEditor(true)} className="bg-brand-blue hover:bg-brand-blue/90 text-white gap-1" data-testid="btn-edit-security">
                <Sliders className="w-3.5 h-3.5" /> Modifica accesso
            </Button>
          )}
        </div>
      )}
      {showEditor && userId && (
        <SecurityEditorModal
          user_id={userId}
          personName={p.identity?.display_name}
          onClose={() => setShowEditor(false)}
          onSaved={() => { setShowEditor(false); load(); onChanged?.(); }}
        />
      )}
      {showChangeEmail && access?.has_account && (
        <ChangeEmailModal
          legacyId={p.legacy_id}
          currentEmail={access.email}
          onClose={() => setShowChangeEmail(false)}
          onSaved={() => { setShowChangeEmail(false); load(); onChanged?.(); }}
        />
      )}
    </div>
  );
}

function confirmText(key) {
  return {
    resend: 'Reinviare l\'email di invito?',
    recover: 'Inviare il link per reimpostare la password? L\'utente sceglierà una nuova password autonomamente.',
    suspend: 'Sospendere l\'accesso? L\'utente non potrà più accedere finché non riattivi.',
    reactivate: 'Riattivare l\'accesso?',
    archive: 'Archiviare l\'account? Le sessioni saranno revocate ma lo storico resta.',
    restore: 'Ripristinare l\'account archiviato?',
    revoke: 'Revocare tutte le sessioni attive dell\'utente?',
  }[key] || 'Confermi?';
}
function successText(key) {
  return {
    resend: 'Invito reinviato',
    recover: 'Email di recupero inviata',
    suspend: 'Account sospeso',
    reactivate: 'Account riattivato',
    archive: 'Account archiviato',
    restore: 'Account ripristinato',
    revoke: 'Sessioni revocate',
  }[key] || 'Operazione riuscita';
}

export function CreateAccessModal({ person, onClose, onDone }) {
  const [email, setEmail] = useState(person?.identity?.email_personal || '');
  const [catalog, setCatalog] = useState(null);
  const primaryAssignment = person?.lavoro?.assignments?.find((a) => a.is_primary && a.status === 'ATTIVA')
    || person?.lavoro?.assignments?.find((a) => a.status === 'ATTIVA')
    || person?.lavoro?.assignments?.[0];
  const [scope, setScope] = useState({
    company_id: person?.lavoro?.company_id || primaryAssignment?.company_id || '',
    location_id: primaryAssignment?.location_id || '',
    department_id: primaryAssignment?.department_id || '',
  });
  const [companies, setCompanies] = useState([]);
  const [locations, setLocations] = useState(null);
  const [departments, setDepartments] = useState(null);
  const [roleCode, setRoleCode] = useState('DIPENDENTE');
  const [overrides, setOverrides] = useState({});
  const [openModule, setOpenModule] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previousOverscrollBehavior = document.body.style.overscrollBehavior;
    document.body.style.overflow = 'hidden';
    document.body.style.overscrollBehavior = 'none';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.overscrollBehavior = previousOverscrollBehavior;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  useEffect(() => {
    Promise.all([
      apiFetch('/api/security/catalog'),
      apiFetch('/api/companies'),
    ])
      .then(([cat, comps]) => { setCatalog(cat); setCompanies(comps); })
      .catch((e) => toast.error(e.message || 'Impossibile caricare ruoli e autorizzazioni'));
  }, []);

  useEffect(() => {
    if (!scope.company_id) { setLocations(null); return undefined; }
    let active = true;
    setLocations(null);
    apiFetch(`/api/locations?company_id=${encodeURIComponent(scope.company_id)}`)
      .then((items) => active && setLocations(items))
      .catch(() => active && setLocations([]));
    return () => { active = false; };
  }, [scope.company_id]);

  useEffect(() => {
    if (!scope.location_id) { setDepartments(null); return undefined; }
    let active = true;
    setDepartments(null);
    apiFetch(`/api/departments?location_id=${encodeURIComponent(scope.location_id)}`)
      .then((items) => active && setDepartments(items))
      .catch(() => active && setDepartments([]));
    return () => { active = false; };
  }, [scope.location_id]);

  const roles = (catalog?.roles || []).filter((role) => !['CEO', 'TITOLARE'].includes(role.code) && role.assignable);
  const capabilitiesByModule = (catalog?.capabilities || []).reduce((groups, capability) => {
    groups[capability.module] = groups[capability.module] || [];
    groups[capability.module].push(capability);
    return groups;
  }, {});
  const capabilityModules = Object.keys(capabilitiesByModule).sort((a, b) => {
    const aIndex = ACCESS_MODULE_ORDER.indexOf(a);
    const bIndex = ACCESS_MODULE_ORDER.indexOf(b);
    if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    return aIndex - bIndex;
  });
  const canDelegate = (code) => {
    if (catalog?.actor_is_global) return true;
    const actorCapabilities = new Set(catalog?.actor_effective_capabilities || []);
    return actorCapabilities.has(code) || actorCapabilities.has(`${code.split('.')[0]}.*`);
  };
  const setOverride = (code, mode) => setOverrides((current) => {
    const next = { ...current };
    if (mode === 'NONE') delete next[code];
    else next[code] = mode;
    return next;
  });

  const submit = async () => {
    if (!email.trim()) { toast.error('Inserisci un\'email'); return; }
    if (!catalog || !roles.some((role) => role.code === roleCode)) {
      toast.error('Ruolo non disponibile nel tuo perimetro autorizzato');
      return;
    }
    setBusy(true);
    try {
      await apiFetch(`/api/personale/${person.legacy_id}/access`, {
        method: 'POST',
        body: JSON.stringify({
          email: email.trim(),
          role_code: roleCode,
          company_id: roleCode === 'DIPENDENTE' ? null : (scope.company_id ?? ''),
          location_id: roleCode === 'DIPENDENTE' ? null : (scope.location_id ?? ''),
          department_id: roleCode === 'DIPENDENTE' ? null : (scope.department_id ?? ''),
          capability_overrides: Object.entries(overrides).map(([capability_code, mode]) => ({
            capability_code,
            mode,
          })),
        }),
      });
      toast.success('Accesso creato · invito inviato');
      onDone();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/40 p-0 sm:items-center sm:p-4"
      data-testid="modal-create-access"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-access-title"
      aria-describedby="create-access-description"
      onClick={onClose}
    >
      <div
        className="flex h-[100dvh] max-h-[100dvh] w-full max-w-2xl flex-col overflow-hidden rounded-none border border-app-border bg-app-surface sm:h-auto sm:max-h-[92vh] sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex shrink-0 items-start justify-between gap-3 border-b border-stone-100 px-4 pb-3 pt-4 sm:px-6 sm:pt-5">
          <div className="min-w-0">
            <div id="create-access-title" className="text-xs uppercase tracking-[0.2em] font-bold text-brand-green">Dai accesso a RestaurantOS</div>
            <div className="mt-1 truncate text-lg font-semibold text-brand-navy">{person.identity?.display_name || 'Persona'}</div>
          </div>
          <button onClick={onClose} aria-label="Chiudi creazione accesso" data-testid="btn-close-create-access" className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-stone-400 hover:bg-stone-50 hover:text-brand-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200">
            <X className="w-5 h-5" />
          </button>
        </header>

        <div
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6 sm:py-5"
          data-testid="create-access-content"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          <div className="space-y-4">
            <section className="rounded-xl border border-stone-200 bg-stone-50 p-3" aria-labelledby="create-access-identity-title">
              <div className="flex gap-2 text-xs text-stone-600">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-brand-green" />
                <div className="min-w-0">
                  <div id="create-access-identity-title" className="font-semibold text-brand-navy">A. Identità persona</div>
                  <div className="mt-0.5 break-words">{person.identity?.display_name || '—'}</div>
                </div>
              </div>
            </section>

            <section aria-labelledby="create-access-account-title">
              <h3 id="create-access-account-title" className="mb-2 text-sm font-bold text-brand-navy">B. Account</h3>
              <Label htmlFor="access-email">Email accesso *</Label>
              <Input id="access-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="input-access-email" placeholder="nome@azienda.it" className="mt-1.5 h-11" />
              <p id="create-access-description" className="mt-1 text-[11px] text-stone-500">Riceverà un'email di benvenuto per creare autonomamente la propria password.</p>
            </section>

            <section aria-labelledby="create-access-role-title">
              <h3 id="create-access-role-title" className="mb-2 text-sm font-bold text-brand-navy">C. Ruolo</h3>
              <Label htmlFor="access-role">Ruolo *</Label>
              <select
                id="access-role"
                value={roleCode}
                onChange={(e) => setRoleCode(e.target.value)}
                disabled={!catalog || busy}
                data-testid="select-access-role"
                className="mt-1.5 h-11 w-full rounded-xl border border-stone-300 bg-white px-3 text-sm text-brand-navy disabled:bg-stone-50"
              >
                {!catalog && <option>Caricamento ruoli…</option>}
                {roles.map((role) => <option key={role.code} value={role.code}>{role.label}</option>)}
              </select>
              <p className="mt-1 text-xs leading-relaxed text-stone-500">
                {catalog?.roles?.find((role) => role.code === roleCode)?.description || ROLE_DESCRIPTIONS[roleCode] || 'Il ruolo applica esclusivamente le autorizzazioni previste dal backend.'}
              </p>
            </section>

            <section className="rounded-xl border border-stone-200 p-3" data-testid="create-access-scope" aria-labelledby="create-access-scope-title">
              <h3 id="create-access-scope-title" className="mb-2 text-sm font-bold text-brand-navy">D. Scope</h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <label className="min-w-0 text-xs text-stone-500">
                  Azienda
                  <select
                    value={scope.company_id}
                    onChange={(e) => setScope({ company_id: e.target.value, location_id: '', department_id: '' })}
                    disabled={roleCode === 'DIPENDENTE' || !catalog || busy}
                    data-testid="create-scope-company"
                    className="mt-1 h-11 w-full min-w-0 rounded-xl border border-stone-300 bg-white px-2 text-sm text-brand-navy disabled:bg-stone-50 disabled:text-stone-400"
                  >
                    <option value="">— seleziona azienda —</option>
                    {companies.map((company) => <option key={company.id} value={company.id}>{company.name}</option>)}
                  </select>
                </label>
                <label className="min-w-0 text-xs text-stone-500">
                  Locale
                  <select
                    value={scope.location_id}
                    onChange={(e) => setScope((current) => ({ ...current, location_id: e.target.value, department_id: '' }))}
                    disabled={roleCode === 'DIPENDENTE' || !scope.company_id || !locations || busy}
                    data-testid="create-scope-location"
                    className="mt-1 h-11 w-full min-w-0 rounded-xl border border-stone-300 bg-white px-2 text-sm text-brand-navy disabled:bg-stone-50 disabled:text-stone-400"
                  >
                    <option value="">— tutti i locali —</option>
                    {(locations || []).map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}
                  </select>
                </label>
                <label className="min-w-0 text-xs text-stone-500">
                  Reparto
                  <select
                    value={scope.department_id}
                    onChange={(e) => setScope((current) => ({ ...current, department_id: e.target.value }))}
                    disabled={roleCode === 'DIPENDENTE' || !scope.location_id || !departments || busy}
                    data-testid="create-scope-department"
                    className="mt-1 h-11 w-full min-w-0 rounded-xl border border-stone-300 bg-white px-2 text-sm text-brand-navy disabled:bg-stone-50 disabled:text-stone-400"
                  >
                    <option value="">— tutti i reparti —</option>
                    {(departments || []).map((department) => <option key={department.id} value={department.id}>{department.name}</option>)}
                  </select>
                </label>
              </div>
              <p className="mt-2 text-[11px] leading-relaxed text-stone-500">
                {roleCode === 'DIPENDENTE'
                  ? 'Il ruolo Dipendente resta limitato alla propria Persona e allo scope derivato.'
                  : 'Il backend verifica che Azienda, Locale e Reparto appartengano al tenant e al perimetro autorizzato.'}
              </p>
            </section>

            {catalog && capabilityModules.length > 0 && (
              <section data-testid="create-access-capabilities" aria-labelledby="create-access-capabilities-title">
                <h3 id="create-access-capabilities-title" className="mb-1 text-sm font-bold text-brand-navy">E. Autorizzazioni</h3>
                <p className="mb-3 text-[11px] leading-relaxed text-stone-500">
                  Le autorizzazioni del ruolo restano attive per default. Puoi aggiungere o bloccare solo capability delegabili dal tuo account.
                </p>
                {capabilityModules.map((module) => {
                  const capabilities = capabilitiesByModule[module];
                  return (
                    <div key={module} className="mb-2 overflow-hidden rounded-xl border border-stone-200">
                      <button
                        type="button"
                        className="flex min-h-11 w-full items-center justify-between gap-3 px-3 py-2.5 text-left text-sm font-semibold text-brand-navy transition-colors hover:bg-stone-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200"
                        aria-expanded={openModule === module}
                        aria-controls={`create-module-content-${module}`}
                        onClick={() => setOpenModule((current) => (current === module ? null : module))}
                        data-testid={`create-module-toggle-${module}`}
                      >
                        <span className="min-w-0 break-words">{accessModuleLabel(module)}</span>
                        <ChevronDown className={`h-4 w-4 shrink-0 text-stone-400 transition-transform ${openModule === module ? 'rotate-180 text-brand-green' : ''}`} aria-hidden="true" />
                      </button>
                      {openModule === module && (
                        <div id={`create-module-content-${module}`} className="space-y-1 border-t border-stone-100 p-3 pt-2" data-testid={`create-module-content-${module}`}>
                          {capabilities.map((capability) => (
                            <label key={capability.code} className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-2 rounded-lg px-1 py-2 text-xs sm:gap-3">
                              <span className="min-w-0 break-words leading-relaxed text-stone-700">{capability.label}</span>
                              <select
                                value={overrides[capability.code] || 'NONE'}
                                onChange={(e) => setOverride(capability.code, e.target.value)}
                                disabled={!canDelegate(capability.code) || busy}
                                data-testid={`access-cap-${capability.code}`}
                                className="h-10 min-w-[7rem] max-w-[8.5rem] shrink-0 rounded-xl border border-stone-300 bg-white px-1.5 text-xs disabled:bg-stone-50 disabled:text-stone-400"
                              >
                                <option value="NONE">Ruolo</option>
                                <option value="GRANT">Attiva</option>
                                <option value="DENY">Blocca</option>
                              </select>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </section>
            )}
          </div>
        </div>

        <footer
          className="flex shrink-0 flex-col-reverse gap-2 border-t border-stone-100 bg-app-surface px-4 py-3 sm:flex-row sm:justify-end sm:px-6"
          data-testid="create-access-footer"
          style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
        >
          <Button variant="outline" onClick={onClose} disabled={busy} className="h-11 w-full sm:w-auto" data-testid="btn-cancel-create-access">Annulla</Button>
          <Button onClick={submit} disabled={busy || !email.trim()} className="h-11 w-full bg-brand-green hover:bg-brand-green-hover text-white gap-2 sm:w-auto" data-testid="btn-submit-create-access">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {busy ? 'Invio invito…' : 'Salva e invia invito'}
          </Button>
        </footer>
      </div>
    </div>
  );
}
